import { ChangeEventHandler, MouseEventHandler, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormInput } from '../components/custom/components'
import PageTitle from '../components/custom/components/PageTitle'
import VerticalLayout from '../components/custom/layouts/Vertical'
import Modal from '../components/Modal'
import Option from '../components/Option'
import { ToastTypes, useToast } from '../components/toast/ToastProvider'
import TripMasterForm from '../components/TripMasterForm'
import { FormType, FormTypeList } from '../components/types'
import { addKey, isInToday } from '../config/functions'
import { AuthUserType } from '../redux/actions/authUserActions'
import { RouteMasterType } from '../redux/actions/routeMasterActions'
import { deleteTripMaster, TripMasterType } from '../redux/actions/tripMasterActions'
import { Key } from '../redux/actions/types'
import { StoreState } from '../redux/reducers'
import { AppDispatch } from '../redux/store'

interface TableBody {
    sno: number
    routeName: string
    tripName: string
    number: string
    startTime: string
    endTime: string
    onEdit: MouseEventHandler<HTMLButtonElement>
    onDelete: MouseEventHandler<HTMLButtonElement>
    timeStamp: string
    authUser: AuthUserType
}

type HandleEdit = (value: TripMasterType) => void
type HandleDelete = (value: TripMasterType) => void


const TripMaster = () => {
    const toast = useToast()
    const dispatch = useDispatch<AppDispatch>()
    const authUser = useSelector<StoreState, AuthUserType>(state => state.authUser)


    const tripMasterList = useSelector<StoreState, Array<TripMasterType & Key>>(state => addKey(state.tripMaster))
    const routeMaster = useSelector<StoreState, Array<RouteMasterType & Key>>(state => addKey(state.routeMaster))
    const [routeId, setRouteId] = useState<string>('')

    const [showForm, setShowForm] = useState<boolean>(false)
    const [formType, setFormType] = useState<FormType>(FormTypeList.ADD)
    const [editData, setEditData] = useState<TripMasterType>()


    const tripMaster = useMemo<Array<TripMasterType & Key>>(() => {
        return routeId !== "" ? tripMasterList.filter(tm => tm.routeId === parseInt(routeId)) : tripMasterList
    }, [routeId, tripMasterList])

    const handleRouteId: ChangeEventHandler<HTMLInputElement> = (e) => {
        setRouteId(e.target.value)
    }

    const handleAdd = () => {
        setShowForm(true)
    }

    const handleEdit: HandleEdit = (value) => {
        setFormType(FormTypeList.UPDATE)
        setEditData(value)
        setShowForm(true)
    }

    const handleDelete: HandleDelete = (value) => {
        if (value.id !== undefined) {
            dispatch(deleteTripMaster(value.id)).then(text => {
                toast(text)
            }).catch(text => {
                toast(text, ToastTypes.ERROR)
            })
        }
    }

    const handleFormClose = () => {
        setShowForm(false)
        setFormType(FormTypeList.ADD)
        setEditData(undefined)
    }

    return <>
        <Modal
            headerText={formType === FormTypeList.ADD ? 'Add Trip Master' : 'Edit Trip Master'}
            visible={showForm}
            onClose={handleFormClose}
            size='lg'
            centered
        >
            <TripMasterForm
                formType={formType}
                editData={editData}
                onSave={handleFormClose}
            />

        </Modal>

        <VerticalLayout>
            <PageTitle
                title='Trip Master'
                buttonText='ADD'
                onButtonClick={handleAdd}
            />

            <div className='card'>
                <div className='card-body'>
                    <div className='container-fluid'>
                        <div className="row">
                            <div className="col-sm-6 col-md-4 col-lg-3">
                                <FormInput
                                    value={routeId}
                                    onChange={handleRouteId}
                                    type='select'
                                    label='Route'
                                >
                                    <option value="">All</option>
                                    {routeMaster.filter(rm => {
                                        return rm.code !== "HIRE"
                                    }).map(rm => {
                                        return <Option value={rm.id} key={rm.key}>{rm.name}</Option>
                                    })}
                                </FormInput>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className='table-wrapper'>
                        <table className='table colored'>
                            <thead>
                                <tr>
                                    <th className='text-truncate align-middle'>S.No</th>
                                    <th className='text-truncate align-middle'>Route Name</th>
                                    <th className='text-truncate align-middle'>Trip Name</th>
                                    <th className='text-truncate align-middle'>Trip Number</th>
                                    <th className='text-truncate align-middle'>Start Time</th>
                                    <th className='text-truncate align-middle'>End Time</th>
                                    <th className='text-truncate align-middle text-center'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tripMaster.map((tm, i) => {
                                    return <TripMasterBody
                                        sno={i + 1}
                                        routeName={tm.routeName}
                                        tripName={tm.tripName}
                                        number={tm.number}
                                        startTime={tm.startTime}
                                        endTime={tm.endTime}
                                        key={tm.key}
                                        authUser={authUser}
                                        timeStamp={tm.createTimestamp !== undefined ? tm.createTimestamp : ''}
                                        onEdit={() => handleEdit(tm)}
                                        onDelete={() => handleDelete(tm)}
                                    />
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </VerticalLayout>
    </>
}

const TripMasterBody = ({ sno, routeName, tripName, number, startTime, endTime, onEdit, onDelete, timeStamp, authUser }: TableBody) => {
    const ShowEditForManager = isInToday(timeStamp)

    return <tr>
        <td>{sno}</td>
        <td>{routeName}</td>
        <td>{tripName}</td>
        <td>{number}</td>
        <td>{startTime}</td>
        <td>{endTime}</td>
        <td>
            <div className='hstack gap-1'>
            {(authUser.role === 'admin' || authUser.role === 'super' || authUser.role === 'Admin' || authUser.role === 'Super') && <button className='btn btn-xs btn-primary' onClick={onEdit}>Edit</button>}
                {(authUser.role === 'Manager' || authUser.role === 'manager') && ShowEditForManager && <button className='btn btn-xs btn-primary' onClick={onEdit}>Edit</button>}
                <button className='btn btn-xs btn-danger' onClick={onDelete}>Delete</button>
            </div>
        </td>
    </tr>
}

export default TripMaster
